// useRelationQuickAdd.js

import { useCallback, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useBaseTypeAccess from 'hooks/useBaseTypeAccess';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { getNodelistNodeTitle } from 'utilities/NodelistField';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';
import { typeAllowsManualCreate } from 'utilities/typeCreatePolicy';

import { findOptionByExactTitle } from 'node/form/fields/nodelist/relationQuickAdd';

/**
 * useRelationQuickAdd
 *
 * Permission gate, sticky footer handlers, exact-title match, and optimistic
 * option merge for single-select Relation pickers.
 *
 * @param {object} params
 * @param {boolean} [params.readOnly]
 * @param {string} params.targetType
 * @param {string|null} params.baseKey
 * @param {Array} params.options - Current select options (including empty).
 * @param {function} params.onSelect - (nodeKey: string) => void
 *
 * @returns {object}
 */
export default function useRelationQuickAdd({
  readOnly = false,
  targetType,
  baseKey = null,
  options = [],
  onSelect,
}) {
  const { getType } = useNodeTypes();
  const { canWriteType } = useBaseTypeAccess();

  const [modalOpen, setModalOpen] = useState(false);
  const [initialTitle, setInitialTitle] = useState('');
  const [createdOptions, setCreatedOptions] = useState([]);

  const resolvedType = String(targetType || '').trim();

  const typeDef = useMemo(() => (
    resolvedType ? getType(resolvedType) : null
  ), [getType, resolvedType]);

  const canQuickAdd = Boolean(
    !readOnly
    && resolvedType
    && baseKey
    && typeAllowsManualCreate(typeDef || resolvedType)
    && canWriteType(resolvedType, typeDef),
  );

  const singularLabel = useMemo(() => (
    getSingularLabel(typeDef, resolvedType || 'Item', 'en') || 'Item'
  ), [resolvedType, typeDef]);

  const footerActionLabel = canQuickAdd ? `+ Add ${singularLabel}` : '';

  const selectOptions = useMemo(() => {
    if (!createdOptions.length) return options;
    const byValue = new Map();
    (Array.isArray(options) ? options : []).forEach((option) => {
      byValue.set(String(option?.value ?? ''), option);
    });
    createdOptions.forEach((option) => {
      const value = String(option?.value ?? '').trim();
      if (!value || byValue.has(value)) return;
      byValue.set(value, option);
    });
    return Array.from(byValue.values());
  }, [createdOptions, options]);

  const handleFooterAction = useCallback((filterQuery) => {
    if (!canQuickAdd) return;
    const draft = String(filterQuery || '').trim();
    if (draft) {
      const match = findOptionByExactTitle(selectOptions, draft);
      if (match?.value) {
        onSelect?.(String(match.value));
        return;
      }
    }
    setInitialTitle(draft);
    setModalOpen(true);
  }, [canQuickAdd, onSelect, selectOptions]);

  const handleCreated = useCallback((node) => {
    const key = String(node?.key || '').trim();
    if (!key) return;

    const { iconKey } = resolveNodeIcon({ node, typeDef });
    setCreatedOptions((prev) => {
      if (prev.some((entry) => entry.value === key)) return prev;
      return [
        ...prev,
        {
          value: key,
          label: getNodelistNodeTitle(node),
          iconKey: iconKey || '',
          note: getNodeSubtitle(node) || '',
        },
      ];
    });
    onSelect?.(key);
  }, [onSelect, typeDef]);

  return {
    canQuickAdd,
    footerActionLabel,
    onFooterAction: canQuickAdd ? handleFooterAction : undefined,
    selectOptions,
    quickAddOpen: modalOpen,
    setQuickAddOpen: setModalOpen,
    quickAddInitialTitle: initialTitle,
    handleCreated,
  };
}
